<template>
  <div class="trend-card" :class="[trendType, { compact }]">
    <!-- COMPACT MODE -->
    <template v-if="compact">
      <q-icon :name="icon" size="0.875rem" />
      <span class="compact-value">{{ formattedPercent }}%</span>
    </template>

    <!-- FULL MODE -->
    <template v-else>
      <div class="trend-icon">
        <q-icon :name="icon" size="2rem" />
      </div>
      
      <div class="trend-content">
        <div class="trend-label">{{ $t(label) }}</div>
        <div class="trend-value">{{ valueText }}</div>
        <div class="trend-description">{{ description }}</div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'TrendCard',
  
  props: {
    trend: { type: Number, required: true },
    label: { type: String, default: 'Overall Trend' },
    threshold: { type: Number, default: 0.05 },
    decimals: { type: Number, default: 2 },
    unit: { type: String, default: 'per year' },
    compact: { type: Boolean, default: false }
  },

  computed: {
    trendType() {
      if (this.trend > this.threshold) return 'positive'
      if (this.trend < -this.threshold) return 'negative'
      return 'neutral'
    },

    icon() {
      return {
        positive: 'trending_up',
        negative: 'trending_down',
        neutral: 'trending_flat'
      }[this.trendType]
    },

    formattedPercent() {
      return (this.trend * 100).toFixed(this.decimals)
    },

    signedPercent() {
      const sign = this.formattedPercent > 0 ? '+' : ''
      return `${sign}${this.formattedPercent}`
    },

    valueText() {
      return `${this.signedPercent}% ${this.$t(this.unit)}`
    },

    description() {
      const abs = Math.abs(this.formattedPercent)
      return {
        positive: `${this.$t('Increasing by')} ${abs}% ${this.$t(this.unit)}`,
        negative: `${this.$t('Decreasing by')} ${abs}% ${this.$t(this.unit)}`,
        neutral: this.$t('Relatively stable over time')
      }[this.trendType]
    }
  }
}
</script>

<style lang="scss" scoped>
.trend-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: var(--radius-md);
  border: 1px solid $grey-4;
  background: linear-gradient(135deg, $grey-2 0%, $grey-3 100%);

  body.body--dark & {
    background: linear-gradient(135deg, $grey-9 0%, $grey-10 100%);
    border-color: $grey-8;
  }

  // Compact variant
  &.compact {
    display: inline-flex;
    gap: 0.125rem;
    padding: 0.125rem 0.375rem;
    border: none;
    border-radius: 0.625rem;
    background: rgba(255, 255, 255, 0.9);
    font-size: 0.625rem;
    font-weight: 600;

    body.body--dark & {
      background: rgba(30, 30, 30, 0.9);
    }
  }

  // Color variants
  &.positive { 
    color: $positive;
    .trend-icon { color: $positive; }
    .trend-value { color: $positive; }
  }
  
  &.negative { 
    color: $negative;
    .trend-icon { color: $negative; }
    .trend-value { color: $negative; }
  }
  
  &.neutral { 
    color: $grey-6;
    .trend-icon { color: $grey-6; }
    .trend-value { color: $grey-6; }
  }
}

// ================================
// COMPACT MODE
// ================================
.compact-value {
  line-height: 1;
}

// ================================
// FULL MODE
// ================================
.trend-icon {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: white;
  box-shadow: var(--shadow-md);

  body.body--dark & {
    background: $grey-9;
  }
}

.trend-content {
  flex: 1;
  min-width: 0;
}

.trend-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: $grey-7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;

  body.body--dark & {
    color: $grey-5;
  }
}

.trend-value {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 0.25rem;
}

.trend-description {
  font-size: 0.8125rem;
  color: $grey-7;
  line-height: 1.3;

  body.body--dark & {
    color: $grey-5;
  }
}

// ================================
// RESPONSIVE
// ================================
@media (max-width: 48rem), (max-height: 48rem)  {
  .trend-icon {
    width: 3rem;
    height: 3rem;
  }

  .trend-value {
    font-size: 1.125rem;
  }
}
</style>